import { useCallback, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/brand";
import BrowserShot from "@/components/BrowserShot";
import CasePreview from "@/components/sections/CasePreview";
import { track } from "@/lib/analytics";
import { homeCases } from "@/lib/offer";

export default function Cases() {
  const [featured, ...rest] = homeCases;
  const [open, setOpen] = useState(null);
  const close = useCallback(() => setOpen(null), []);
  const show = (item) => {
    track("case_preview_open", { case: item.name });
    setOpen(item);
  };

  return (
    <section id="cases" data-testid="cases-section" className="py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <h2 className="text-3xl font-bold tracking-[-0.03em] text-foreground md:text-5xl">
            Projetos no ar agora
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
            Cada um resolve um objetivo de negócio: vender, agendar, posicionar autoridade ou
            simplificar a operação.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <button
              type="button"
              onClick={() => show(featured)}
              data-testid="case-card-0"
              className="group flex h-full w-full flex-col rounded-xl bg-primary p-3 text-left text-primary-foreground"
            >
              <BrowserShot
                src={featured.fullImage}
                alt={`Site ${featured.name}`}
                url={featured.url}
                motion="hover"
                className="border-white/10"
              />
              <div className="flex flex-1 items-start justify-between gap-6 p-5 md:p-7">
                <div>
                  <p className="text-sm font-medium text-white/70">{featured.type}</p>
                  <h3 className="mt-1 text-2xl font-bold tracking-[-0.02em] md:text-3xl">
                    {featured.name}
                  </h3>
                  {featured.result && (
                    <p className="mt-2 text-lg font-semibold text-accent">{featured.result}</p>
                  )}
                  <p className="mt-3 max-w-md leading-relaxed text-white/80">{featured.summary}</p>
                  <p className="mt-5 text-sm font-semibold text-white">Ver projeto completo</p>
                </div>
                <ArrowUpRight className="h-6 w-6 shrink-0 text-accent transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </div>
            </button>
          </Reveal>

          <div className="grid gap-6 lg:col-span-5">
            {rest.map((item, i) => (
              <Reveal key={item.name} delay={0.08 * (i + 1)}>
                <button
                  type="button"
                  onClick={() => show(item)}
                  data-testid={`case-card-${i + 1}`}
                  className="group grid w-full grid-cols-[minmax(0,2fr)_minmax(0,3fr)] items-center gap-5 rounded-xl border border-border p-3 text-left transition-colors hover:border-primary/40"
                >
                  <div className="overflow-hidden rounded-lg border border-border bg-muted">
                    <img
                      src={item.fullImage}
                      alt={`Site ${item.name}`}
                      loading="lazy"
                      width="960"
                      height="3600"
                      className="aspect-[4/3] w-full object-cover object-top shot-hoverscroll"
                    />
                  </div>
                  <div className="pr-2">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-primary">
                      {item.type}
                      {item.demo && (
                        <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                          Projeto demonstrativo
                        </span>
                      )}
                    </p>
                    <h3 className="mt-0.5 flex items-center gap-1.5 text-lg font-semibold tracking-[-0.01em] text-foreground">
                      {item.name}
                      <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
                    </h3>
                    {item.result && (
                      <p className="mt-0.5 text-sm font-semibold text-foreground">{item.result}</p>
                    )}
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.summary}</p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal className="mt-8">
          <Link
            to="/projetos"
            data-testid="cases-all-projects"
            className="group inline-flex items-center gap-1.5 py-2 text-sm font-semibold text-primary"
          >
            Ver todos os projetos
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </Reveal>
      </div>

      <CasePreview item={open} onClose={close} />
    </section>
  );
}
